import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { useCalibrationTritium } from "@app/pages/calibrations/utils/useCalibration";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import {
  useEquipment,
  useEquipmentMide,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

const CountingFe55 = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { currentCounting } = useAnalysis();
  const filteredProcedures = useFilteredProcedures("CON");
  const { loading } = useEquipmentMide();
  const { fechtCalibrationTritiumByAnalysisType, calibrationTritiumFiltered } =
    useCalibrationTritium();

  const lisentSqpTsieWhite = useMemo(
    () => form.watch("sqp_tsie_white"),
    [form]
  );
  const lisentSqpTsieSample = useMemo(
    () => form.watch("sqp_tsie_sample"),
    [form]
  );
  const countingValidValue = useMemo(
    () => form.watch("counting_valid"),
    [form]
  );
  const countingDate = form.watch("counting_date");
  const countingEquipment = form.watch("counting_equipment");

  const cpmSample = useMemo(() => form.watch("sample_cpm"), [form]);
  const cpmSampleQuimioluminiscencia = useMemo(
    () => form.watch("cpm_sample_quimioluminiscencia"),
    [form]
  );
  const grossWhiteCpm = useMemo(() => form.watch("gross_white_cpm"), [form]);
  const quimioluminiscenciaWhite = useMemo(
    () => form.watch("cmp_white_quimioluminiscencia"),
    [form]
  );

  useEffect(() => {
    const typeAnalysis = form.getValues("typeAnalysis");
    if (typeAnalysis?.id) {
      fechtCalibrationTritiumByAnalysisType(typeAnalysis.id);
    }
  }, []);

  const validCalibration = useMemo(() => {
    if (!calibrationTritiumFiltered || !countingDate) return null;
    const countingDateObj = new Date(countingDate);
    return (
      calibrationTritiumFiltered
        .filter(
          (cal) =>
            cal.date &&
            new Date(cal.date) < countingDateObj &&
            (!countingEquipment?.id || cal.equipment?.id === countingEquipment.id)
        )
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        )[0] ?? null
    );
  }, [calibrationTritiumFiltered, countingDate, countingEquipment]);

  useEffect(() => {
    const sqpTsieWhite = parseFloat(form.getValues("sqp_tsie_white"));
    const sqpTsieSample = parseFloat(form.getValues("sqp_tsie_sample"));

    if (!isNaN(sqpTsieWhite) && !isNaN(sqpTsieSample)) {
      const tsiePatron =
        validCalibration?.tsie_sqp_patron_without_agent_extintor;
      const isCalibrationValid =
        tsiePatron !== undefined && !isNaN(Number(tsiePatron))
          ? Number(tsiePatron) > sqpTsieWhite &&
            Number(tsiePatron) > sqpTsieSample
          : true;

      form.setValue(
        "counting_valid",
        isCalibrationValid ? "S" : "N"
      );
    } else {
      form.setValue("counting_valid", "");
    }
  }, [lisentSqpTsieWhite, lisentSqpTsieSample, validCalibration]);

  useEffect(() => {
    const totalSampleCpm =
      parseFloat(cpmSample) - parseFloat(cpmSampleQuimioluminiscencia);
    if (!isNaN(totalSampleCpm)) {
      form.setValue(
        "total_cpm_sample",
        totalSampleCpm.toFixed(2).replace(",", ".")
      );
    }
  }, [cpmSample, cpmSampleQuimioluminiscencia]);

  useEffect(() => {
    const totalWhiteCpm =
      parseFloat(grossWhiteCpm) - parseFloat(quimioluminiscenciaWhite);
    if (!isNaN(totalWhiteCpm)) {
      const formattotalWhiteCpm = totalWhiteCpm.toFixed(2).replace(",", ".");

      form.setValue("total_cpm_white", formattotalWhiteCpm);
    }
  }, [grossWhiteCpm, quimioluminiscenciaWhite]);

  const stylesInput = useMemo(() => {
    if (countingValidValue === "N") {
      return { backgroundColor: "#ef233c" };
    } else if (countingValidValue === "S") {
      return { backgroundColor: "#a7c957" };
    }
    return {};
  }, [countingValidValue]);

  return (
    <>
      {currentCounting.index !== undefined ? (
        <React.Fragment key={currentCounting.id}>
          <ResponsiveFormContainer columns={5}>
            <MAutocomplete
              name="counting_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentCounting.canEdit}
            />

            <MAutocomplete
              name="counting_procedure"
              options={filteredProcedures}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              validation={{ required: "El procedimiento es requerido" }}
              disabled={!currentCounting.canEdit}
            />
            <MAutocomplete
              key={`counting_equipment-${form.getValues("counting_equipment")}`}
              name="counting_equipment"
              options={equipmentList}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              loading={loading}
              disabled={!currentCounting.canEdit}
            />
            <MDatePicker
              key="counting_date"
              name="counting_date"
              pickerType="date"
              label="Fecha de contaje"
              validation={{
                required: "La fecha de contaje es requerida",
              }}
              disabled={!currentCounting.canEdit}
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={5} mt={2}>
            <MTextField
              name="sqp_tsie_white"
              placeholder="SQP/TSIE Blanco"
              label="SQP/TSIE Blanco"
              type="number"
              validation={{ required: "El SQP/TSIE Blanco es requerido" }}
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />

            <MTextField
              name="sqp_tsie_sample"
              placeholder="SQP/TSIE Muestra"
              label="SQP/TSIE Muestra"
              type="number"
              validation={{ required: "El SQP/TSIE Muestra es requerido" }}
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />

            <MTextField
              name="counting_valid"
              placeholder="Contaje Válido"
              label="Contaje Válido"
              type="text"
              fullWidth
              disabled
              style={stylesInput}
            />

            <MTextField
              name="gross_white_cpm"
              placeholder="cpm Blanco"
              label="cpm Blanco"
              validation={{ required: "El cpm Blanco es requerido" }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="cmp_white_quimioluminiscencia"
              placeholder="cpm Blanco quimioluminiscencia"
              label="cpm Blanco quimioluminiscencia"
              type="number"
              validation={{
                required: "El cpm Blanco quimioluminiscencia es requerido",
              }}
              fullWidth
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              key={JSON.stringify(form.watch("total_cpm_white"))}
              name="total_cpm_white"
              placeholder="cpm Blanco final"
              label="cpm Blanco final"
              type="text"
              disabled
              fullWidth
            />

            <MTextField
              name="sample_cpm"
              placeholder="cpm Muestra"
              label="cpm Muestra"
              validation={{ required: "El cpm Muestra es requerido" }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="cpm_sample_quimioluminiscencia"
              placeholder="cpm Muestra quimioluminiscencia"
              label="cpm Muestra quimioluminiscencia"
              type="number"
              validation={{
                required: "El cpm Muestra quimioluminiscencia es requerido",
              }}
              fullWidth
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              key={JSON.stringify(form.watch("total_cpm_sample"))}
              name="total_cpm_sample"
              placeholder="cpm Muestra final"
              label="cpm Muestra final"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              name="total_white_counting_time"
              placeholder="Tiempo total de contaje del blanco (min)"
              label="Tiempo total de contaje del blanco (min)"
              validation={{
                required: "El tiempo total de contaje del blanco es requerido",
              }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="total_sample_counting_time"
              placeholder="Tiempo total de contaje de la muestra (min)"
              label="Tiempo total de contaje de la muestra (min)"
              validation={{
                required:
                  "El tiempo total de contaje de la muestra es requerido",
              }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
          </ResponsiveFormContainer>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default CountingFe55;
